/**
 * Registrar Suplidores — migración web de ares8505.
 *
 * Mantenimiento de los suplidores de cuentas por pagar (tabla suplidores):
 * listado, alta y modificación. A diferencia del escritorio (que actualizaba
 * el registro localizándolo por RNC sin filtrar el tipo y no permitía cambiar
 * el nombre), la web actualiza por id, por lo que también permite editar el
 * nombre del suplidor.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin suplidores registrados
      </Typography>
    </Stack>
  );
}

const suplidorVacio = {
  descrip: "",
  direccion: "",
  ciudad: "",
  telefono: "",
  fax: "",
  email: "",
  rnc: "",
};

export default function SuplidoresCxp({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [rows, setRows] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/cxp_suplidores", {});
      if (ok) {
        setRows((data.suplidores || []).map((r) => ({ ...r, id: r.id })));
      } else {
        showAlert({
          message: data.Error || "No se pudieron listar los suplidores",
          color: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const cargarConfig = async () => {
    const { ok, data } = await post("/api/cxp_config", {});
    if (ok) setProvincias(data.provincias || []);
  };

  useEffect(() => {
    cargar();
    cargarConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Alta / edición ----
  const [editando, setEditando] = useState(null); // null=cerrado, 0=nuevo, id=editar
  const [form, setForm] = useState(suplidorVacio);
  const [guardando, setGuardando] = useState(false);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const abrirNuevo = () => {
    setForm(suplidorVacio);
    setEditando(0);
  };

  const abrirEditar = (row) => {
    setForm({
      descrip: row.descrip || "",
      direccion: row.direccion || "",
      ciudad: row.ciudad || "",
      telefono: row.telefono || "",
      fax: row.fax || "",
      email: row.email || "",
      rnc: row.rnc || "",
    });
    setEditando(row.id);
  };

  const guardar = async () => {
    if (!form.descrip.trim()) {
      showAlert({ message: "El nombre del suplidor no puede estar en blanco", color: "warning" });
      return;
    }
    if (!form.direccion.trim()) {
      showAlert({ message: "La dirección no puede estar en blanco", color: "warning" });
      return;
    }
    if (!form.telefono.trim()) {
      showAlert({ message: "El teléfono no puede estar en blanco", color: "warning" });
      return;
    }
    if (!form.rnc.trim()) {
      showAlert({ message: "El RNC no puede estar en blanco", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const body = {
        descrip: form.descrip.trim(),
        direccion: form.direccion.trim(),
        ciudad: form.ciudad,
        telefono: form.telefono.trim(),
        fax: form.fax.trim(),
        email: form.email.trim(),
        rnc: form.rnc.trim(),
      };
      const esNuevo = editando === 0;
      const { ok, data } = await post(
        esNuevo ? "/api/cxp_suplidor_guardar" : "/api/cxp_suplidor_modificar",
        esNuevo ? body : { id: editando, ...body }
      );
      if (ok) {
        showAlert({
          message: esNuevo ? "Suplidor registrado" : "Suplidor modificado",
          color: "success",
        });
        setEditando(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar el suplidor", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "descrip", headerName: "Nombre", flex: 1, minWidth: 200 },
    { field: "rnc", headerName: "RNC", width: 130 },
    { field: "telefono", headerName: "Teléfono", width: 130 },
    { field: "fax", headerName: "WhatsApp", width: 130 },
    { field: "email", headerName: "Email", width: 180 },
    { field: "ciudad", headerName: "Ciudad", width: 140 },
    {
      field: "acciones",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => abrirEditar(params.row)}
          sx={btnOutline(FP.color.navy)}
        >
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Box component={embedded ? "section" : "main"} sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}>
      {!embedded && (
        <PageHeader
          title="Registrar Suplidores"
          subtitle="Mantenimiento de los suplidores de cuentas por pagar"
          cta={
            <Button startIcon={<AddIcon />} onClick={abrirNuevo} sx={btnOutline(FP.color.navy)}>
              Nuevo suplidor
            </Button>
          }
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        {embedded && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button startIcon={<AddIcon />} onClick={abrirNuevo} sx={btnOutline(FP.color.navy)}>
              Nuevo suplidor
            </Button>
          </Stack>
        )}
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} suplidores
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Alta / edición de suplidor */}
      <Dialog
        open={editando !== null}
        onClose={() => setEditando(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editando === 0 ? "Nuevo suplidor" : "Editar suplidor"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={form.descrip}
                onChange={(e) => setForm((f) => ({ ...f, descrip: e.target.value.toUpperCase() }))}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Dirección"
                value={form.direccion}
                onChange={setCampo("direccion")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                select
                fullWidth
                label="Ciudad"
                value={form.ciudad}
                onChange={setCampo("ciudad")}
                sx={fpInputSx}
              >
                {provincias.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={form.telefono}
                onChange={setCampo("telefono")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp"
                value={form.fax}
                onChange={setCampo("fax")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={form.email}
                onChange={setCampo("email")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RNC"
                value={form.rnc}
                onChange={(e) => setForm((f) => ({ ...f, rnc: e.target.value.replace(/\D/g, "") }))}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditando(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
